import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import app from './firebase';
import {
  CountRow,
  InventoryBatch,
  OperationNotification,
  OperationalTask,
  StockMovement,
  StockPosition,
  TaskTemplate,
} from '../types/operations';

const functions = getFunctions(app);
export async function operation<T = Record<string, unknown>>(
  name: string,
  data: object,
  options?: { timeout: number },
): Promise<T> {
  const response = await httpsCallable<object, T>(functions, name, options)(data);
  return response.data;
}

export interface Dashboard {
  alertDays: number[];
  tasks: OperationalTask[];
  templates: TaskTemplate[];
  notifications: OperationNotification[];
  profiles: { id: string; name: string; role: string }[];
  stores: {
    id: string;
    name: string;
    inventoryVersion: number;
    caretakerIds: string[];
  }[];
  batches: InventoryBatch[];
  positions: StockPosition[];
  movements: {
    id: string;
    equipmentId: string;
    quantity: number;
    reason: string;
  }[];
}
export type DashboardScope = 'full' | 'stock' | 'store' | 'directory';
const pendingDashboards = new Map<string, Promise<Dashboard>>();
// Share only requests in flight, never stock snapshots after they have settled.
export function dashboard(
  storeId?: string,
  scope: DashboardScope = storeId ? 'stock' : 'full',
): Promise<Dashboard> {
  const key = JSON.stringify([getAuth(app).currentUser?.uid, scope, storeId]);
  const pending = pendingDashboards.get(key);
  if (pending) return pending;
  const request = operation<Dashboard>('operationsDashboard', { storeId, scope })
    .finally(() => { pendingDashboards.delete(key); });
  pendingDashboards.set(key, request);
  return request;
}
export const saveMovement = (movement: StockMovement) =>
  operation('inventoryOperation', { action: 'move', ...movement });
export const taskAction = (taskId: string, action: string, data: object = {}) =>
  operation('taskOperation', { taskId, action, ...data });
export const getDraft = (taskId: string) =>
  operation<{ rows: CountRow[] }>('taskDraft', { taskId });
export const newRequestId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
